import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, CalendarDays, Tag } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { blogRequest, formatDate, imageUrl } from "../lib/blogApi";

export default function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page") || 1));
  const tag = searchParams.get("tag") || "";
  const [data, setData] = useState({ posts: [], pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Blog | SEO & Website Development Insights | RankifyDev";
    let active = true;
    setLoading(true);
    setError("");
    blogRequest(`/posts?page=${page}&limit=9${tag ? `&tag=${encodeURIComponent(tag)}` : ""}`)
      .then((result) => { if (active) setData(result); })
      .catch((err) => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page, tag]);

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="pt-20">
        <section className="relative overflow-hidden border-b border-black/5 py-16 sm:py-20">
          <div className="absolute inset-0 brand-gradient-soft" aria-hidden="true" />
          <div className="absolute -right-16 -top-20 h-72 w-72 rounded-full brand-gradient blur-3xl opacity-20" aria-hidden="true" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <span className="inline-flex rounded-full border border-[hsl(var(--brand-violet)/.15)] bg-white/80 px-4 py-1.5 text-xs font-semibold text-brand-deep">RANKIFYDEV INSIGHTS</span>
            <h1 className="mt-5 max-w-4xl text-4xl font-bold leading-tight text-[hsl(var(--brand-navy))] sm:text-5xl lg:text-6xl">
              Ideas to help your <span className="text-gradient-brand">business grow.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
              Practical insights on SEO, website development, content strategy, and growing your online visibility.
            </p>
          </div>
        </section>

        <section className="py-12 sm:py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {loading && <div className="py-20 text-center text-slate-500">Loading articles...</div>}
            {!loading && error && <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">Unable to load blog posts: {error}. Please check the backend API configuration.</div>}
            {!loading && !error && data.posts.length === 0 && (
              <div className="rounded-3xl border border-black/5 bg-white px-6 py-16 text-center card-shadow">
                <h2 className="text-2xl font-bold text-[hsl(var(--brand-navy))]">Our blog is getting ready</h2>
                <p className="mx-auto mt-3 max-w-lg text-slate-600">New articles will appear here after they are published from the CMS.</p>
              </div>
            )}
            {!loading && !error && data.posts.length > 0 && (
              <>
                <div className="mb-8 flex items-end justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-wider text-brand">Latest articles</p>
                    <h2 className="mt-2 text-2xl font-bold text-[hsl(var(--brand-navy))] sm:text-3xl">{tag ? `Articles tagged "${tag}"` : "Fresh from RankifyDev"}</h2>
                  </div>
                  <span className="text-sm text-slate-500">{data.total} articles</span>
                </div>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {data.posts.map((post) => (
                    <article key={post._id} className="group overflow-hidden rounded-2xl border border-black/5 bg-white card-shadow transition-all duration-300 hover:-translate-y-1 hover:card-shadow-hover">
                      <Link to={`/blog/${post.slug}`} className="block">
                        <div className="aspect-[16/10] overflow-hidden bg-lilac">
                          {post.featureImage ? (
                            <img src={imageUrl(post.featureImage)} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                          ) : (
                            <div className="flex h-full items-center justify-center brand-gradient-soft text-4xl font-bold text-brand/40">R.</div>
                          )}
                        </div>
                      </Link>
                      <div className="p-6">
                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                          {post.publishedAt && <span className="inline-flex items-center gap-1.5"><CalendarDays size={14}/>{formatDate(post.publishedAt)}</span>}
                          {post.tags?.[0] && <span className="inline-flex items-center gap-1.5"><Tag size={14}/>{post.tags[0]}</span>}
                        </div>
                        <h3 className="mt-4 text-xl font-bold leading-snug text-[hsl(var(--brand-navy))] transition-colors group-hover:text-brand">
                          <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                        </h3>
                        {post.excerpt && <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600">{post.excerpt}</p>}
                        <Link to={`/blog/${post.slug}`} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand hover:gap-3 transition-all">Read article <ArrowRight size={16}/></Link>
                      </div>
                    </article>
                  ))}
                </div>
                {data.pages > 1 && (
                  <div className="mt-10 flex items-center justify-center gap-3">
                    <button disabled={page <= 1} onClick={() => setSearchParams({ ...(tag ? { tag } : {}), ...(page > 2 ? { page: String(page - 1) } : {}) })} className="rounded-full border px-5 py-2 text-sm font-semibold disabled:opacity-40">Previous</button>
                    <span className="text-sm text-slate-500">Page {page} of {data.pages}</span>
                    <button disabled={page >= data.pages} onClick={() => setSearchParams({ ...(tag ? { tag } : {}), page: String(page + 1) })} className="rounded-full border px-5 py-2 text-sm font-semibold disabled:opacity-40">Next</button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
